import React, { useEffect, useRef, useState } from 'react'
import config from '../../config'
import {
	GOOGLE_TRANSLATE_COOKIE_NAME,
	LANGUAGE_TO_GOOGLE_LANGUAGE,
} from '../../constants/translationConstants'
import { useSelector } from 'react-redux'
import { useGoogleTranslate } from '../../utility/hooks'
import { AVAILABLE_LANGUAGES } from '@mission.io/mission-toolkit/constants'
import { getCurrentStudentLanguage } from '../../store/selectors/sharedSelectors'

/**
 * HiddenGoogleTranslate - handles translating the page to other languages
 *
 * @return {React$Node}
 */
export default function HiddenGoogleTranslate(): React$Node {
	const [hasRefBeenSet, setHasRefBeenSet] = useState(false)
	const [needsToLoadGoogleTranslate, setNeedsToLoadGoogleTranslate] = useState(false)
	const studentLanguage = useSelector(getCurrentStudentLanguage)
	const googleRef = useRef()

	useEffect(() => {
		if (!studentLanguage) {
			return
		}

		const newGoogleLanguage = LANGUAGE_TO_GOOGLE_LANGUAGE[studentLanguage]

		config.googleTranslateCookieDomains.forEach(domain => {
			if (studentLanguage === AVAILABLE_LANGUAGES.ENGLISH) {
				eraseCookie(GOOGLE_TRANSLATE_COOKIE_NAME, domain)
				return
			}
			createCookie(GOOGLE_TRANSLATE_COOKIE_NAME, `/en/${newGoogleLanguage}`, 365, domain)
		})

		if (studentLanguage !== AVAILABLE_LANGUAGES.ENGLISH) {
			setNeedsToLoadGoogleTranslate(true)
		}

		const previousLanguageFromStore = localStorage.getItem('languageValue')
		const previouslySetLanguage = AVAILABLE_LANGUAGES.hasOwnProperty(previousLanguageFromStore)
			? previousLanguageFromStore
			: AVAILABLE_LANGUAGES.ENGLISH

		if (previouslySetLanguage === studentLanguage) {
			// nothing to change
			return
		}

		localStorage.setItem('languageValue', studentLanguage)
	}, [studentLanguage])

	return (
		<div className="hidden pointer-events-none">
			<div
				id="google_translate_element"
				ref={ref => {
					googleRef.current = ref
					if (!hasRefBeenSet) {
						setHasRefBeenSet(true)
					}
				}}
			/>
			{needsToLoadGoogleTranslate ? (
				<LoadGoogleTranslate currentLanguage={studentLanguage ?? AVAILABLE_LANGUAGES.ENGLISH} />
			) : null}
		</div>
	)
}

/**
 * LoadGoogleTranslate - load google translate to translate the page into a new language
 *
 * @param {Object} props - the react props
 * @param {$Keys<typeof AVAILABLE_LANGUAGES>} props.currentLanguage - the language the page should be translated into
 *
 * @return {Rect$Node}
 */
function LoadGoogleTranslate({
	currentLanguage,
}: {
	currentLanguage: $Keys<typeof AVAILABLE_LANGUAGES>,
}) {
	useGoogleTranslate(currentLanguage)
	return null
}

/**
 * Creates a cookie on the browser page.
 * Primary purpose of this function is to help erase an existing cookie on the browser.
 * @param {string} name name of cookie
 * @param {string} value value of cookie
 * @param {number} days time the cookie is valid for
 * @param {string} domain the web url domain that the cookie applies to.
 */
export function createCookie(name: string, value: string, days: number, domain: string) {
	let expires
	if (days) {
		var date = new Date()
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
		expires = '; expires=' + date.toISOString()
	} else {
		expires = ''
	}
	document.cookie = name + '=' + value + expires + '; domain=' + domain + '; path=/'
}

/**
 * Erases a cookie from the browser with the given name and domain.
 * @param {string} name name of the cookie
 * @param {string} domain the web url domain which the cookie applies to.
 */
function eraseCookie(name, domain) {
	createCookie(name, '', -1, domain)
}
